import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, from, of, throwError } from 'rxjs';
import { ResourceType } from '@app/shared/enums/resource-type';
import { env } from '../../../environments/env';
import { BaseService, Resource } from '@app/core/services/base.service';
import { OfflineDataDb } from '@app/core/db/offline-data';
import { LoaderService } from '@app/header/loader/loader.service';
import { WorkOrderDto, WorkOrderObjectDto } from '@app/generated/model';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class WorkOrderService extends BaseService {
  constructor(
    http: HttpClient,
    private offlineData: OfflineDataDb,
    private loaderService: LoaderService,
  ) {
    super(http);
  }

  @Resource(ResourceType.LoadWorkOrder)
  loadWorkOrder(id: string): Observable<WorkOrderDto> {
    const encodedId = encodeURIComponent(id);
    return this.http
      .get<WorkOrderDto>(`${env.get().api.url}/work-orders/${encodedId}`)
      .pipe(
        catchError((error) =>
          this.loadOfflineData<WorkOrderDto>(id, 'WORK_ORDER').pipe(catchError(() => throwError(() => error))),
        ),
      );
  }

  @Resource(ResourceType.LoadWorkOrderOperations)
  loadWorkOrderOperations(id: string): Observable<WorkOrderObjectDto[]> {
    const encodedId = encodeURIComponent(id);
    return this.http
      .get<WorkOrderObjectDto[]>(`${env.get().api.url}/work-order/${encodedId}/objects/hierarchy`)
      .pipe(catchError((error) => this.loadOfflineDataWhenOffline<WorkOrderObjectDto[]>(id, 'OBJECTS', [], error)));
  }

  @Resource(ResourceType.UpdateWorkLineProgress)
  updateWorkLineProgress(ids: string[], workOrderId: string, progress: number): Observable<void> {
    return this.http.put<void>(`${env.get().api.url}/work-order/${workOrderId}/workLines/progress/${progress}`, ids);
  }

  @Resource(ResourceType.UpdateWorkLineQuantity)
  updateWorkLineQuantity(ids: string, workOrderId: string, quantity: number): Observable<void> {
    return this.http.put<void>(
      `${env.get().api.url}/work-order/${workOrderId}/workLine/${ids}/quantity/${quantity}`,
      null,
    );
  }

  @Resource(ResourceType.UpdateWorkLineCableLength)
  updateWorkLineCableLength(
    workLineId: string,
    workOrderId: string,
    length: number,
    externalLocationId: string,
  ): Observable<void> {
    const encodedWorkOrderId = encodeURIComponent(workOrderId);
    const encodedWorkLineId = encodeURIComponent(workLineId);
    const encodedExternalLocationId = encodeURIComponent(externalLocationId);

    return this.http.put<void>(
      `${
        env.get().api.url
      }/work-order/${encodedWorkOrderId}/workLine/${encodedWorkLineId}/cableLength/${length}/externalLocationId/${encodedExternalLocationId}`,
      null,
    );
  }

  private loadOfflineData<T>(workOrderId: string, type: string): Observable<T> {
    return from(this.offlineData.workOrderDataItems.where({ workOrderId, type }).first()).pipe(
      switchMap((item) => {
        if (item?.data != null) {
          return of(item.data as T);
        }

        return throwError(() => new Error(`No offline ${type} data found for work order ${workOrderId}`));
      }),
    );
  }

  /**
   * Only falls back to downloaded (IndexedDB) data when the app is genuinely offline, confirmed by
   * the same check-update probe that drives the offline banner. On a reachable server error (e.g.
   * HTTP 500) the empty state is returned instead of stale offline data, matching the behaviour
   * used when nothing was downloaded.
   */
  private loadOfflineDataWhenOffline<T>(
    workOrderId: string,
    type: string,
    emptyValue: T,
    error: unknown,
  ): Observable<T> {
    const reachable$ = navigator.onLine ? from(this.loaderService.confirmBackendReachable()) : of(false);
    return reachable$.pipe(
      switchMap((reachable) =>
        reachable
          ? of(emptyValue)
          : this.loadOfflineData<T>(workOrderId, type).pipe(catchError(() => throwError(() => error))),
      ),
    );
  }
}
